import { BoldText, Link, SmallText, Text, grayBase, tealDark700 } from "@RooUI";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm, useFormContext, useFormState } from "react-hook-form";
import useFormPersist from "react-hook-form-persist";
import { useHistory } from "react-router-dom";

import { ControlledHearAboutRooSelect } from "../Common/Controllers/ControlledHearAboutRooSelect";
import { ControlledPhoneInput } from "../Common/Controllers/ControlledPhoneInput";
import FullAddressAutoComplete from "../Common/Form/AddressAutocomplete/FullAddressAutocomplete";
import { getInputErrorMessage } from "../Common/Form/service";
import { openMavenChat } from "../Common/mavenChatUtils";
import { useRegistrationContext as useVetTechRegistrationContext } from "../RegistrationNew/Components/Common/registrationContext";
import { checkForDuplicateHospital } from "../api/hospital";
import { AuthUtility } from "../helpers/AuthUtility";
import HospitalProfileValueProp from "../static/images/HospitalProfileValueProp.png";
import { Event, EventTracker } from "../tracking/service/EventTracker/EventTrackerService";

import RegistrationPage from "./RegistrationPage";
import { HOSPITAL_REGISTRATION_LOCALSTORAGE_KEY } from "./constants";
import { useRegistrationContext } from "./registrationContext";
import { getHospitalProfileRegistrationFormSchema } from "./registrationForm";
import {
  ButtonContainer,
  ErrorAlert,
  RegistrationButton,
  RegistrationForm as RegistrationFormComponent,
} from "./styles";
import { RegistrationForm } from "./types";
import { aTypeOfHospitalOptions } from "@Roo/constants/dropDownConstants";
import { ControlledSelect } from "@Roo/Common/Controllers";

function CreateHospitalProfile() {
  const history = useHistory();
  const { setRegistrationContext, registrationContext } = useRegistrationContext();
  const {
    setRegistrationContext: setVetTechRegistrationContext,
    registrationContext: vetTechRegistrationContext,
  } = useVetTechRegistrationContext();

  const { handleSubmit, control, watch } = useFormContext<RegistrationForm>();
  const { errors } = useFormState<RegistrationForm>({ control });
  const [duplicateAddressWarning, setDuplicateAddressWarning] = useState(false);
  const [loading, setLoading] = useState(false);

  const hospitalAddress = watch("hospitalAddress");
  const hospitalCity = watch("hospitalCity");
  const hospitalZipcode = watch("hospitalZipcode");
  const hospitalState = watch("hospitalState");

  useEffect(() => {
    if (vetTechRegistrationContext.userId && vetTechRegistrationContext.userId !== -1) {
      localStorage.clear();
      // @ts-expect-error TS2322
      setVetTechRegistrationContext({ registrationContext: {} });
      AuthUtility.setIsAuthorisedUser(false);
      AuthUtility.clearPreviousSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkDuplicateHospitalAddress = async () => {
      if (hospitalAddress && hospitalCity && hospitalZipcode && hospitalState) {
        setLoading(true);

        try {
          const isDuplicateHospital = await checkForDuplicateHospital(
            hospitalAddress,
            hospitalCity,
            hospitalZipcode,
            hospitalState
          );

          setLoading(false);

          if (isDuplicateHospital) {
            setDuplicateAddressWarning(true);
            EventTracker.send({
              eventName: Event.Name.DUPLICATE_ADDRESS_REGISTRATION_ATTEMPT,
              eventType: Event.Type.IMPRESSION,
              entityType: Event.Entity.HOSPITAL,
              entityId: undefined,
              context: {
                hospitalAddress: hospitalAddress,
                hospitalCity: hospitalCity,
                hospitalZipcode: hospitalZipcode,
                hospitalStateId: hospitalState,
              },
            });
          } else {
            setDuplicateAddressWarning(false);
          }
        } catch (error) {
          setLoading(false);
          setDuplicateAddressWarning(false);
          Sentry.captureException(error, {
            extra: { context: "Error checking duplicate hospital" },
          });
        }
      }
    };
    checkDuplicateHospitalAddress();
  }, [hospitalAddress, hospitalCity, hospitalZipcode, hospitalState]);

  const onContinue = async (data: RegistrationForm) => {
    setRegistrationContext({ ...registrationContext, ...data });
    history.push({
      pathname: "/register/hospitalAccount",
    });
  };
  return (
    <RegistrationPage
      title="Set up your hospital's profile"
      subtitle="Vets need this information to request shifts"
      progressBarPercent={(2 / 4) * 100}
      previousPage={{ title: "Sign Up", pathname: "/register" }}
      rightContainerContent={
        <>
          <BoldText color={tealDark700}>
            Our Roo-bust network of providers are waiting to pick up shifts!
          </BoldText>
          <img src={HospitalProfileValueProp} />
        </>
      }
      leftContainerContent={
        <>
          {duplicateAddressWarning && (
            <ErrorAlert
              $alertStatus="warning"
              alertBody={
                <Text data-testid="duplicatedHospitalMsg">
                  We found a hospital that appears to be similar to the one you're registering. If
                  your hospital is already registered with Roo, please reach out to your team for an
                  invitation to join instead of registering for a new account. For any other
                  questions, <Link onClick={() => openMavenChat()}>reach out to us</Link>.
                </Text>
              }
            />
          )}
          <RegistrationFormComponent className="xxl-gap">
            <FullAddressAutoComplete
              control={control}
              addressName="hospitalAddress"
              cityName="hospitalCity"
              stateName="hospitalState"
              zipcodeName="hospitalZipcode"
              defaultValues={{
                address: registrationContext.hospitalAddress,
                city: registrationContext.hospitalCity,
                state: registrationContext.hospitalState,
                zipcode: registrationContext.hospitalZipcode,
              }}
            />
            <div>
              <ControlledSelect
                name="typeOfHospital"
                control={control}
                label="Type of hospital"
                options={aTypeOfHospitalOptions}
              />
              <SmallText color={grayBase}>
                Please select the correct hospital type as any future updates will have to be
                completed by a Roo rep.
              </SmallText>
            </div>
            <ControlledPhoneInput
              name="hospitalPhoneNumber"
              control={control}
              label="Hospital phone number"
              // @ts-expect-error TS2322
              error={getInputErrorMessage(errors?.hospitalPhoneNumber?.message)}
              defaultValue={registrationContext.hospitalPhoneNumber}
            />
            <ControlledHearAboutRooSelect control={control} label="How did you hear about us?" />
          </RegistrationFormComponent>
          <ButtonContainer data-testid="hospitalProfileContinue">
            <RegistrationButton
              $buttonType="primary"
              onClick={handleSubmit(onContinue)}
              loading={loading}
              disabled={loading}
            >
              Continue
            </RegistrationButton>
          </ButtonContainer>
        </>
      }
    />
  );
}

const CreateHospitalProfileContainer = () => {
  const methods = useForm<RegistrationForm>({
    resolver: zodResolver(getHospitalProfileRegistrationFormSchema()),
  });

  useFormPersist(HOSPITAL_REGISTRATION_LOCALSTORAGE_KEY, {
    watch: methods.watch,
    setValue: methods.setValue,
    storage: window.localStorage,
  });

  return (
    <FormProvider {...methods}>
      <CreateHospitalProfile />
    </FormProvider>
  );
};
// eslint-disable-next-line no-restricted-exports
export default CreateHospitalProfileContainer;
